<template>
  <div class="bms-simulator-container">
    <div class="simulator-header">
      <h5>BMS Simulator</h5>
      <p class="simulator-description">Compare algorithm versions for plant simulation</p>
    </div>

    <!-- Today's Date Warning -->
    <div v-if="isToday" class="today-warning">
      <div class="warning-icon">⚠️</div>
      <div class="warning-content">
        <h6>Simulation Not Available for Today</h6>
        <p>BMS Simulator comparison is only available for previous days. Please select a past date to run simulations and compare algorithm performance.</p>
      </div>
    </div>

    <!-- All other content - only show if not today -->
    <div v-if="!isToday">
      <!-- Version Selection -->
      <div class="version-selection-container">
      <div class="version-selection-header">
        <h6>Select Algorithm Versions to Compare</h6>
        <p class="selection-info">Minimum 2 versions, maximum all available</p>
      </div>
      
      <div class="version-checkboxes">
        <div 
          v-for="version in availableVersions" 
          :key="version"
          class="version-checkbox-item"
        >
          <input 
            type="checkbox" 
            :id="`version-${version}`"
            :value="version"
            v-model="selectedVersions"
            @change="handleVersionSelection"
            :disabled="isLoading"
            class="version-checkbox"
          />
          <label :for="`version-${version}`" class="version-label">
            {{ version }}
            <span v-if="version === 'v1.2.0'" class="current-version-badge">Current</span>
          </label>
        </div>
      </div>
      
      <div class="version-actions">
        <div class="selection-buttons">
          <button 
            @click="selectAllVersions" 
            :disabled="isLoading"
            class="custom-button select-all-btn"
          >
            Select All
          </button>
          <button 
            @click="clearSelection" 
            :disabled="isLoading"
            class="custom-button clear-btn"
          >
            Clear All
          </button>
        </div>
        
        <div class="action-divider"></div>
        
        <div class="compare-button-container">
          <button 
            @click="runSimulation" 
            :disabled="isLoading || !canRunSimulation"
            class="custom-button compare-btn"
          >
            Compare
          </button>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="loading-container">
      <div class="loading-content">
        <div class="loading-spinner-large">
          <div class="spinner-ring"></div>
          <div class="spinner-ring"></div>
          <div class="spinner-ring"></div>
        </div>
        <h5 class="loading-title">Running BMS Simulation</h5>
        <p class="loading-text">Processing algorithm comparisons for {{ selectedVersions.length }} version(s)...</p>
        <div class="loading-progress">
          <div class="progress-bar">
            <div class="progress-fill"></div>
          </div>
          <span class="progress-text">Please wait while we analyze the data</span>
        </div>
      </div>
    </div>

    <!-- Results Display -->
    <div v-if="!isLoading && simulationResults.length > 0" class="results-container">
      <!-- Summary Metrics -->
      <div class="metrics-summary">
        <h6>Simulation Summary</h6>
        <div class="metrics-grid">
          <div 
            v-for="(result, index) in simulationResults" 
            :key="index"
            class="metric-card"
          >
            <div class="metric-header">
              <h6>{{ result.algo_type }}</h6>
              <span class="metric-status" :class="result.status">{{ result.status }}</span>
            </div>
            <div class="metric-values">
              <div class="metric-item">
                <span class="metric-label">Total Battery Discharge (kWh)</span>
                <span class="metric-value">{{ result.totalDischarge || 'N/A' }}</span>
              </div>
              <div class="metric-item">
                <span class="metric-label">Total Battery Charge (kWh)</span>
                <span class="metric-value">{{ result.totalCharge || 'N/A' }}</span>
              </div>
              <div class="metric-item">
                <span class="metric-label">Peak Battery Discharge (kW)</span>
                <span class="metric-value">{{ result.peakDischarge || 'N/A' }}</span>
              </div>
              <div class="metric-item">
                <span class="metric-label">Peak Battery Charge (kW)</span>
                <span class="metric-value">{{ result.peakCharge || 'N/A' }}</span>
              </div>
              <div class="metric-item">
                <span class="metric-label">Data Completeness (%)</span>
                <span class="metric-value">{{ result.efficiency || 'N/A' }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Chart Type Selection -->
      <div class="chart-controls">
        <h6>Chart Visualization</h6>
        <div class="chart-type-selector">
          <button 
            v-for="chartType in chartTypes" 
            :key="chartType.value"
            :class="{ active: selectedChartType === chartType.value }"
            @click="selectChartType(chartType.value)"
            class="chart-type-btn"
          >
            {{ chartType.label }}
          </button>
        </div>
      </div>

      <!-- Plant Level Charts -->
      <div v-if="selectedChartType === 'plant'" class="plant-level-section">
        <h6>Plant Level Analysis</h6>
        
        <!-- Battery Power Chart -->
        <div class="chart-section">
          <h6 class="chart-subtitle">Battery Power Comparison</h6>
          <div class="chart-container">
            <CustomChart
              ref="batteryChart"
              chartTitle="BMS Simulator - Battery Power Comparison"
              chartType="line"
              dataUnit="kW"
              :datasets="batteryChartDatasets"
              :hiddenLegends="hiddenLegends"
            />
          </div>
        </div>

        <!-- SOC Chart -->
        <div class="chart-section">
          <h6 class="chart-subtitle">State of Charge (SOC) Comparison</h6>
          <div class="chart-container">
            <CustomChart
              ref="socChart"
              chartTitle="BMS Simulator - SOC Comparison"
              chartType="line"
              dataUnit="%"
              :datasets="socChartDatasets"
              :hiddenLegends="hiddenLegends"
            />
          </div>
        </div>

        <!-- Cumulative Cost Chart -->
        <div class="chart-section">
          <h6 class="chart-subtitle">Cumulative Cost Comparison</h6>
          <div class="chart-container">
            <CustomChart
              ref="costChart"
              chartTitle="BMS Simulator - Cumulative Cost Comparison"
              chartType="line"
              dataUnit="AUD"
              :datasets="costChartDatasets"
              :hiddenLegends="hiddenLegends"
            />
          </div>
        </div>

        <!-- Total Daily Cost Display -->
        <div class="total-cost-section">
          <h6 class="section-title">Total Daily Energy Cost</h6>
          <div class="total-cost-grid">
            <div 
              v-for="(result, index) in simulationResults" 
              :key="`cost-${index}`"
              class="total-cost-card"
              :class="{ 'top-performer': isTopPerformer(index) }"
            >
              <div class="cost-card-header">
                <span class="algorithm-name">{{ result.algo_type }}</span>
                <span v-if="isTopPerformer(index)" class="best-badge">Best</span>
              </div>
              <div class="cost-value-container">
                <span class="currency-symbol">$</span>
                <span class="cost-value">{{ getTotalDailyCost(result) }}</span>
              </div>
              <div class="cost-label">Total Daily Cost (AUD)</div>
            </div>
          </div>
        </div>

        <!-- Cost Comparison Row -->
        <div class="cost-comparison-row">
          <h6 class="comparison-title">Cost Comparison</h6>
          <div class="comparison-grid">
            <div 
              v-for="(result, index) in simulationResults" 
              :key="`comparison-${index}`"
              class="comparison-card"
              :class="{ 
                'best-cost': isTopPerformer(index),
                'worst-cost': isWorstPerformer(index)
              }"
            >
              <div class="comparison-header">
                <span class="comparison-algo-name">{{ result.algo_type }}</span>
                <span v-if="isTopPerformer(index)" class="comparison-badge best">Best</span>
                <span v-else-if="isWorstPerformer(index)" class="comparison-badge worst">Highest</span>
              </div>
              <div class="comparison-content">
                <div class="comparison-cost">
                  <span class="cost-amount">${{ getTotalDailyCost(result) }}</span>
                </div>
                <div class="comparison-percentages">
                  <template v-for="(otherResult, otherIndex) in simulationResults" :key="`vs-${otherIndex}`">
                    <div 
                      v-if="index !== otherIndex"
                      class="percentage-item"
                    >
                      <span class="percentage-label">vs {{ otherResult.algo_type }}:</span>
                      <span 
                        class="percentage-value"
                        :class="getPercentageClass(index, otherIndex)"
                      >
                        {{ getCostPercentageDifference(result, otherResult) }}
                      </span>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Device Level Charts -->
      <div v-if="selectedChartType === 'devices'" class="device-charts-section">
        <h6>Device Level Analysis</h6>
        <div class="device-charts-grid">
          <div 
            v-for="(deviceData, deviceId) in deviceChartsData" 
            :key="deviceId"
            class="device-chart-container"
          >
            <div class="device-chart-header">
              <h6>{{ deviceId }}</h6>
              <span class="device-info">{{ deviceData.algorithmCount }} algorithm(s)</span>
            </div>
            
            <!-- Battery Power Chart -->
            <div class="device-chart-wrapper">
              <h6 class="chart-subtitle">Battery Power</h6>
              <CustomChart
                :ref="`deviceChart_${deviceId}`"
                :chartTitle="`Device ${deviceId} - Battery Power`"
                chartType="line"
                dataUnit="kW"
                :datasets="deviceData.datasets"
                :hiddenLegends="hiddenLegends"
              />
            </div>

            <!-- SOC Chart (if data available) -->
            <div v-if="deviceData.socDatasets && deviceData.socDatasets.length > 0" class="device-chart-wrapper">
              <h6 class="chart-subtitle">State of Charge (SOC)</h6>
              <CustomChart
                :ref="`deviceSocChart_${deviceId}`"
                :chartTitle="`Device ${deviceId} - SOC`"
                chartType="line"
                dataUnit="%"
                :datasets="deviceData.socDatasets"
                :hiddenLegends="hiddenLegends"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Data Export -->
      <div class="export-section">
        <button @click="exportData" class="custom-button export-btn">
          Export Data
        </button>
      </div>
    </div>

    <!-- Error State -->
    <div v-if="errorMessage" class="error-container">
      <p class="error-text">{{ errorMessage }}</p>
      <button @click="retrySimulation" class="custom-button retry-btn">
        Retry
      </button>
    </div>

    <!-- No Data State -->
    <div v-if="!isLoading && simulationResults.length === 0 && !errorMessage" class="no-data-container">
      <p class="no-data-text">Select algorithm versions to run simulation</p>
    </div>
    </div> <!-- End of !isToday conditional -->
  </div>
</template>

<script>
import { apiRequest } from '@/services/apiService';
import CustomChart from '@/components/cards/CustomChart.vue';
import LoadingSpinner from '@/components/cards/LoadingSpinner.vue';

export default {
  name: 'BmsSimulator',
  components: {
    CustomChart,
    LoadingSpinner,
  },
  props: {
    plantId: {
      type: Number,
      required: true,
    },
    dateStr: {
      type: String,
      required: true,
      default: () => {
        // Fallback to today's date if not provided
        const today = new Date();
        const year = today.getFullYear();
        const month = (today.getMonth() + 1).toString().padStart(2, '0');
        const day = today.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
    },
  },
  data() {
    return {
      availableVersions: [
        'auto',
        'v1.1.3-plant',
        'v1.2.0',
        'v1.2.0-jcs',
        'v1.2.1',
        'v1.2.2'
      ],
      selectedVersions: [],
      simulationResults: [],
      batteryChartDatasets: [],
      costChartDatasets: [],
      socChartDatasets: [],
      devicesChartDatasets: [],
      deviceChartsData: {},
      hiddenLegends: [],
      isLoading: false,
      errorMessage: '',
      subDataUrl: "/api/v1.1/data",
      selectedChartType: 'plant',
      chartTypes: [
        { value: 'plant', label: 'Plant Level' },
        { value: 'devices', label: 'Device Level' }
      ],
    };
  },

  computed: {
    isToday() {
      const today = new Date();
      const year = today.getFullYear();
      const month = (today.getMonth() + 1).toString().padStart(2, '0');
      const day = today.getDate().toString().padStart(2, '0');
      const todayStr = `${year}-${month}-${day}`;
      return this.dateStr === todayStr;
    },
    canRunSimulation() {
      return this.selectedVersions.length >= 2 && this.selectedVersions.length <= this.availableVersions.length;
    },
    topPerformerIndices() {
      if (this.simulationResults.length === 0) return [];
      
      let minCost = Infinity;
      const bestIndices = [];
      
      // First pass: find the minimum cost
      this.simulationResults.forEach((result, index) => {
        const cost = this.getTotalDailyCost(result);
        if (cost !== 'N/A') {
          const costValue = parseFloat(cost);
          if (!isNaN(costValue)) {
            if (costValue < minCost) {
              minCost = costValue;
            }
          }
        }
      });
      
      // Second pass: collect all indices with the minimum cost
      this.simulationResults.forEach((result, index) => {
        const cost = this.getTotalDailyCost(result);
        if (cost !== 'N/A') {
          const costValue = parseFloat(cost);
          if (!isNaN(costValue) && Math.abs(costValue - minCost) < 0.01) { // Use small epsilon for floating point comparison
            bestIndices.push(index);
          }
        }
      });
      
      return bestIndices;
    },
    worstPerformerIndices() {
      if (this.simulationResults.length === 0) return [];
      
      let maxCost = -Infinity;
      const worstIndices = [];
      
      // First pass: find the maximum cost
      this.simulationResults.forEach((result, index) => {
        const cost = this.getTotalDailyCost(result);
        if (cost !== 'N/A') {
          const costValue = parseFloat(cost);
          if (!isNaN(costValue)) {
            if (costValue > maxCost) {
              maxCost = costValue;
            }
          }
        }
      });
      
      // Second pass: collect all indices with the maximum cost
      this.simulationResults.forEach((result, index) => {
        const cost = this.getTotalDailyCost(result);
        if (cost !== 'N/A') {
          const costValue = parseFloat(cost);
          if (!isNaN(costValue) && Math.abs(costValue - maxCost) < 0.01) { // Use small epsilon for floating point comparison
            worstIndices.push(index);
          }
        }
      });
      
      return worstIndices;
    },
  },
  methods: {
    handleVersionSelection() {
      // Clear previous results when selection changes
      if (this.selectedVersions.length < 2) {
        this.simulationResults = [];
        this.batteryChartDatasets = [];
        this.costChartDatasets = [];
        this.socChartDatasets = [];
        this.devicesChartDatasets = [];
        this.deviceChartsData = {};
      }
    },

    selectAllVersions() {
      this.selectedVersions = [...this.availableVersions];
    },

    clearSelection() {
      this.selectedVersions = [];
      this.simulationResults = [];
      this.batteryChartDatasets = [];
      this.costChartDatasets = [];
      this.socChartDatasets = [];
      this.devicesChartDatasets = [];
      this.deviceChartsData = {};
    },

    selectChartType(chartType) {
      this.selectedChartType = chartType;
    },

    async runSimulation() {
      if (!this.canRunSimulation) return;

      this.isLoading = true;
      this.errorMessage = '';
      this.simulationResults = [];
      this.batteryChartDatasets = [];
      this.costChartDatasets = [];
      this.socChartDatasets = [];
      this.devicesChartDatasets = [];
      this.deviceChartsData = {};

      try {
        // Run simulations for all selected versions in parallel
        const simulationPromises = this.selectedVersions.map(version => 
          this.fetchSimulationData(version)
        );

        const results = await Promise.all(simulationPromises);
        
        // Filter out failed simulations
        this.simulationResults = results.filter(result => result !== null);
        
        if (this.simulationResults.length > 0) {
          this.prepareAllChartData();
        } else {
          this.errorMessage = 'No simulation data could be retrieved';
        }
      } catch (error) {
        console.error('Simulation error:', error);
        this.errorMessage = 'Failed to run simulation. Please try again.';
      } finally {
        this.isLoading = false;
      }
    },

    async fetchSimulationData(algoType) {
      try {
        console.log('Fetching simulation data for:', algoType);
        console.log('Plant ID:', this.plantId);
        console.log('Date String:', this.dateStr);
        
        // Ensure we have a valid date string
        const dateStr = this.dateStr || new Date().toISOString().split('T')[0];
        
        const bodyData = {
          plant_id: this.plantId,
          date_str: dateStr,
          algo_type: algoType,
        };
        
        console.log('Request body:', bodyData);

        const url = `${this.subDataUrl}/proxy/simulator/plants/daily/get`;
        const response = await apiRequest('post', url, bodyData);

        console.log(`Full response for ${algoType}:`, response);
        
        if (response.status === 'success' && response.data) {
          console.log(`Successfully fetched data for ${algoType}:`, response.data);
          console.log('Plant data:', response.data.plant);
          console.log('Battery power data:', response.data.plant?.batP);
          
          return {
            algo_type: algoType,
            data: response.data,
            status: 'success',
            totalDischarge: this.calculateTotalDischarge(response.data),
            totalCharge: this.calculateTotalCharge(response.data),
            peakDischarge: this.calculatePeakDischarge(response.data),
            peakCharge: this.calculatePeakCharge(response.data),
            efficiency: this.calculateEfficiency(response.data),
          };
        } else {
          console.warn(`Failed to fetch data for ${algoType}:`, response);
          return {
            algo_type: algoType,
            status: 'failed',
            error: response.message || 'Unknown error',
          };
        }
      } catch (error) {
        console.error(`Error fetching simulation data for ${algoType}:`, error);
        return {
          algo_type: algoType,
          status: 'failed',
          error: error.message,
        };
      }
    },

    calculateTotalDischarge(data) {
      console.log('Calculating total discharge for data:', data);
      if (!data || !data.plant || !data.plant.batP) {
        console.log('Missing data structure:', { data: !!data, plant: !!data?.plant, batP: !!data?.plant?.batP });
        return null;
      }
      
      // Sum only positive battery power values (discharge) and convert to energy (assuming 5-minute intervals)
      const totalDischargePower = data.plant.batP.reduce((sum, power) => {
        const powerValue = parseFloat(power || 0);
        if (isNaN(powerValue)) return sum;
        // Only add positive values (discharge)
        return sum + (powerValue > 0 ? powerValue : 0);
      }, 0);
      
      console.log('Total discharge power calculated:', totalDischargePower);
      // Convert from kW to kWh (5-minute intervals = 1/12 hour)
      const dischargeEnergy = (totalDischargePower / 12).toFixed(2);
      console.log('Discharge energy calculated:', dischargeEnergy);
      return dischargeEnergy;
    },

    calculateTotalCharge(data) {
      console.log('Calculating total charge for data:', data);
      if (!data || !data.plant || !data.plant.batP) {
        console.log('Missing data structure:', { data: !!data, plant: !!data?.plant, batP: !!data?.plant?.batP });
        return null;
      }
      
      // Sum only negative battery power values (charge) and convert to energy (assuming 5-minute intervals)
      const totalChargePower = data.plant.batP.reduce((sum, power) => {
        const powerValue = parseFloat(power || 0);
        if (isNaN(powerValue)) return sum;
        // Only add negative values (charge) - convert to positive for display
        return sum + (powerValue < 0 ? Math.abs(powerValue) : 0);
      }, 0);
      
      console.log('Total charge power calculated:', totalChargePower);
      // Convert from kW to kWh (5-minute intervals = 1/12 hour)
      const chargeEnergy = (totalChargePower / 12).toFixed(2);
      console.log('Charge energy calculated:', chargeEnergy);
      return chargeEnergy;
    },

    calculatePeakDischarge(data) {
      if (!data || !data.plant || !data.plant.batP) return null;
      
      // Find the maximum positive power value (discharge)
      const dischargePowers = data.plant.batP.map(power => {
        const powerValue = parseFloat(power || 0);
        return isNaN(powerValue) ? 0 : (powerValue > 0 ? powerValue : 0);
      });
      
      const maxDischarge = Math.max(...dischargePowers);
      return maxDischarge.toFixed(2);
    },

    calculatePeakCharge(data) {
      if (!data || !data.plant || !data.plant.batP) return null;
      
      // Find the maximum negative power value (charge) and convert to positive for display
      const chargePowers = data.plant.batP.map(power => {
        const powerValue = parseFloat(power || 0);
        return isNaN(powerValue) ? 0 : (powerValue < 0 ? Math.abs(powerValue) : 0);
      });
      
      const maxCharge = Math.max(...chargePowers);
      return maxCharge.toFixed(2);
    },

    calculateEfficiency(data) {
      if (!data || !data.plant || !data.plant.batP) return null;
      
      // Simple efficiency calculation based on data consistency
      const validPoints = data.plant.batP.filter(power => {
        const powerValue = parseFloat(power);
        return !isNaN(powerValue);  // Only exclude NaN, include 0
      });
      
      return ((validPoints.length / data.plant.batP.length) * 100).toFixed(1);
    },

    getTotalDailyCost(result) {
      if (result.status !== 'success' || !result.data || !result.data.plant || !result.data.plant.cost || !result.data.plant.cost.cumulative) {
        return 'N/A';
      }
      
      const cumulativeCosts = result.data.plant.cost.cumulative;
      if (!cumulativeCosts || cumulativeCosts.length === 0) {
        return 'N/A';
      }
      
      // The last value in cumulative cost array represents the total daily cost
      const totalCost = parseFloat(cumulativeCosts[cumulativeCosts.length - 1]);
      
      if (isNaN(totalCost)) {
        return 'N/A';
      }
      
      // Format to 2 decimal places
      return totalCost.toFixed(2);
    },

    getCostPercentageDifference(result1, result2) {
      const cost1 = this.getTotalDailyCost(result1);
      const cost2 = this.getTotalDailyCost(result2);
      
      if (cost1 === 'N/A' || cost2 === 'N/A') {
        return 'N/A';
      }
      
      const cost1Value = parseFloat(cost1);
      const cost2Value = parseFloat(cost2);
      
      if (isNaN(cost1Value) || isNaN(cost2Value) || cost2Value === 0) {
        return 'N/A';
      }
      
      // Calculate percentage difference: ((cost1 - cost2) / cost2) * 100
      const percentageDiff = ((cost1Value - cost2Value) / cost2Value) * 100;
      
      if (percentageDiff > 0) {
        return `+${percentageDiff.toFixed(2)}%`;
      } else if (percentageDiff < 0) {
        return `${percentageDiff.toFixed(2)}%`;
      } else {
        return '0.00%';
      }
    },

    getPercentageClass(index1, index2) {
      const result1 = this.simulationResults[index1];
      const result2 = this.simulationResults[index2];
      
      const cost1 = this.getTotalDailyCost(result1);
      const cost2 = this.getTotalDailyCost(result2);
      
      if (cost1 === 'N/A' || cost2 === 'N/A') {
        return '';
      }
      
      const cost1Value = parseFloat(cost1);
      const cost2Value = parseFloat(cost2);
      
      if (isNaN(cost1Value) || isNaN(cost2Value) || cost2Value === 0) {
        return '';
      }
      
      const percentageDiff = ((cost1Value - cost2Value) / cost2Value) * 100;
      
      if (percentageDiff > 0) {
        return 'percentage-higher';
      } else if (percentageDiff < 0) {
        return 'percentage-lower';
      } else {
        return 'percentage-equal';
      }
    },

    isTopPerformer(index) {
      return this.topPerformerIndices.includes(index);
    },

    isWorstPerformer(index) {
      return this.worstPerformerIndices.includes(index);
    },

    prepareAllChartData() {
      const colors = [
        'rgba(75, 192, 192, 0.8)',
        'rgba(255, 99, 132, 0.8)',
        'rgba(54, 162, 235, 0.8)',
        'rgba(255, 205, 86, 0.8)',
        'rgba(153, 102, 255, 0.8)',
        'rgba(255, 159, 64, 0.8)',
        'rgba(199, 199, 199, 0.8)',
        'rgba(83, 102, 255, 0.8)',
      ];

      console.log('Preparing all chart data for results:', this.simulationResults);
      
      // Prepare battery power chart data
      this.batteryChartDatasets = this.prepareBatteryChartData(colors);
      
      // Prepare cumulative cost chart data
      this.costChartDatasets = this.prepareCostChartData(colors);
      
      // Prepare SOC chart data
      this.socChartDatasets = this.prepareSocChartData(colors);
      
      // Prepare device level chart data (legacy single chart)
      this.devicesChartDatasets = this.prepareDevicesChartData(colors);
      
      // Prepare separate device charts data
      this.deviceChartsData = this.prepareDeviceChartsData(colors);
    },

    prepareBatteryChartData(colors) {
      return this.simulationResults.map((result, index) => {
        if (result.status !== 'success' || !result.data || !result.data.plant) return null;

        const powerValues = result.data.plant.batP.map(power => {
          const powerValue = parseFloat(power || 0);
          return isNaN(powerValue) ? 0 : powerValue;
        });

        const timeLabels = this.generateTimeLabels(powerValues.length);

        return {
          group: 'simulator_data',
          name: `${result.algo_type} Battery Power`,
          values: powerValues,
          color: colors[index % colors.length],
          labels: timeLabels,
        };
      }).filter(dataset => dataset !== null);
    },

    prepareCostChartData(colors) {
      return this.simulationResults.map((result, index) => {
        if (result.status !== 'success' || !result.data || !result.data.plant || !result.data.plant.cost) return null;

        const costValues = result.data.plant.cost.cumulative.map(cost => {
          const costValue = parseFloat(cost || 0);
          return isNaN(costValue) ? 0 : costValue;
        });

        const timeLabels = this.generateTimeLabels(costValues.length);

        return {
          group: 'simulator_data',
          name: `${result.algo_type} Cumulative Cost`,
          values: costValues,
          color: colors[index % colors.length],
          labels: timeLabels,
        };
      }).filter(dataset => dataset !== null);
    },

    prepareSocChartData(colors) {
      return this.simulationResults.map((result, index) => {
        if (result.status !== 'success' || !result.data || !result.data.plant || !result.data.plant.soc) return null;

        const socValues = result.data.plant.soc.map(soc => {
          const socValue = parseFloat(soc || 0);
          return isNaN(socValue) ? 0 : socValue * 100; // Convert to percentage
        });

        const timeLabels = this.generateTimeLabels(socValues.length);

        return {
          group: 'simulator_data',
          name: `${result.algo_type} SOC`,
          values: socValues,
          color: colors[index % colors.length],
          labels: timeLabels,
        };
      }).filter(dataset => dataset !== null);
    },

    prepareDevicesChartData(colors) {
      const deviceDatasets = [];
      let colorIndex = 0;

      this.simulationResults.forEach((result) => {
        if (result.status !== 'success' || !result.data || !result.data.devices) return;

        // Process each device in the response
        Object.keys(result.data.devices).forEach(deviceId => {
          const device = result.data.devices[deviceId];
          if (device && device.batP) {
            const powerValues = device.batP.map(power => {
              const powerValue = parseFloat(power || 0);
              return isNaN(powerValue) ? 0 : powerValue;
            });

            const timeLabels = this.generateTimeLabels(powerValues.length);

            deviceDatasets.push({
              group: 'device_data',
              name: `${result.algo_type} - ${deviceId}`,
              values: powerValues,
              color: colors[colorIndex % colors.length],
              labels: timeLabels,
            });

            colorIndex++;
          }
        });
      });

      return deviceDatasets;
    },

    prepareDeviceChartsData(colors) {
      const deviceCharts = {};
      let colorIndex = 0;

      this.simulationResults.forEach((result) => {
        if (result.status !== 'success' || !result.data || !result.data.devices) return;

        // Process each device in the response
        Object.keys(result.data.devices).forEach(deviceId => {
          const device = result.data.devices[deviceId];
          if (device && device.batP) {
            // Initialize device chart if it doesn't exist
            if (!deviceCharts[deviceId]) {
              deviceCharts[deviceId] = {
                datasets: [],
                socDatasets: [],
                algorithmCount: 0
              };
            }

            // Battery Power data
            const powerValues = device.batP.map(power => {
              const powerValue = parseFloat(power || 0);
              return isNaN(powerValue) ? 0 : powerValue;
            });

            // SOC data (if available)
            let socValues = [];
            if (device.soc) {
              socValues = device.soc.map(soc => {
                const socValue = parseFloat(soc || 0);
                return isNaN(socValue) ? 0 : socValue * 100; // Convert to percentage
              });
            }

            const timeLabels = this.generateTimeLabels(powerValues.length);

            deviceCharts[deviceId].datasets.push({
              group: 'device_data',
              name: result.algo_type,
              values: powerValues,
              color: colors[colorIndex % colors.length],
              labels: timeLabels,
            });

            // Add SOC dataset if available
            if (socValues.length > 0) {
              deviceCharts[deviceId].socDatasets.push({
                group: 'device_soc_data',
                name: `${result.algo_type} SOC`,
                values: socValues,
                color: colors[colorIndex % colors.length],
                labels: timeLabels,
              });
            }

            deviceCharts[deviceId].algorithmCount++;
            colorIndex++;
          }
        });
      });

      console.log('Prepared device charts data:', deviceCharts);
      return deviceCharts;
    },

    generateTimeLabels(length) {
      const labels = [];
      for (let i = 0; i < length; i++) {
        const minutes = i * 5;
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        labels.push(`${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`);
      }
      return labels;
    },

    exportData() {
      let datasets = [];
      let filename = '';
      
      // Get datasets based on selected chart type
      switch (this.selectedChartType) {
        case 'plant':
          // Combine battery power, SOC, and cost datasets for plant level export
          datasets = [...this.batteryChartDatasets, ...this.socChartDatasets, ...this.costChartDatasets];
          filename = `BMS_Simulator_PlantLevel_${this.plantId}_${this.dateStr}.csv`;
          break;
        case 'devices':
          datasets = this.devicesChartDatasets;
          filename = `BMS_Simulator_Devices_${this.plantId}_${this.dateStr}.csv`;
          break;
        default:
          datasets = this.batteryChartDatasets;
          filename = `BMS_Simulator_${this.plantId}_${this.dateStr}.csv`;
      }

      if (datasets.length === 0) {
        alert('No data to export');
        return;
      }

      let csvContent = 'Time';
      
      // Add column headers
      datasets.forEach(dataset => {
        csvContent += `,${dataset.name}`;
      });
      csvContent += '\n';

      // Get the maximum length of all datasets
      const maxLength = Math.max(...datasets.map(dataset => dataset.values.length));
      
      // Add data rows
      for (let i = 0; i < maxLength; i++) {
        const timeLabel = datasets[0]?.labels?.[i] || `${Math.floor(i/12)}:${String((i*5)%60).padStart(2, '0')}`;
        csvContent += timeLabel;
        
        datasets.forEach(dataset => {
          const value = dataset.values[i] !== undefined ? dataset.values[i] : '';
          csvContent += `,${value}`;
        });
        csvContent += '\n';
      }

      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    retrySimulation() {
      this.errorMessage = '';
      this.runSimulation();
    },
  },
  watch: {
    plantId() {
      this.clearSelection();
    },
    dateStr() {
      this.clearSelection();
    },
  },
};
</script>

<style scoped>
.bms-simulator-container {
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 2px 10px var(--shadow-color);
  padding: 1.5rem;
  margin: 1rem 0;
}

.simulator-header {
  text-align: center;
  margin-bottom: 2rem;
  border-bottom: 2px solid var(--border-color);
  padding-bottom: 1rem;
}

.simulator-header h5 {
  margin: 0 0 0.5rem 0;
  color: var(--text-primary);
  font-weight: 600;
}

.simulator-description {
  margin: 0;
  color: var(--text-tertiary);
  font-size: 0.9rem;
}

.version-selection-container {
  background: var(--bg-secondary);
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 2rem;
}

.version-selection-header {
  margin-bottom: 1rem;
}

.version-selection-header h6 {
  margin: 0 0 0.5rem 0;
  color: var(--text-primary);
  font-weight: 600;
}

.selection-info {
  margin: 0;
  color: var(--text-tertiary);
  font-size: 0.85rem;
}

.version-checkboxes {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.version-checkbox-item {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  transition: all 0.2s ease;
}

.version-checkbox-item:hover {
  border-color: var(--custom-color1, #4CAF50);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.version-checkbox {
  width: 18px;
  height: 18px;
  margin-right: 0.75rem;
  cursor: pointer;
  accent-color: var(--custom-color1, #4CAF50);
  background-color: var(--input-bg);
  border: 2px solid var(--border-color);
}

.version-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  cursor: pointer;
  font-size: 0.9rem;
  color: var(--text-primary);
  user-select: none;
}

.current-version-badge {
  background: var(--custom-color1, #4CAF50);
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
}

.version-actions {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
}

.selection-buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.action-divider {
  width: 60px;
  height: 2px;
  background: linear-gradient(90deg, transparent, var(--border-color), transparent);
  margin: 0.5rem 0;
}

.compare-button-container {
  display: flex;
  justify-content: center;
}

.loading-container {
  text-align: center;
  padding: 2rem;
}

.loading-text {
  margin-top: 1rem;
  color: var(--text-secondary);
  font-size: 0.9rem;
}

.results-container {
  margin-top: 2rem;
}

.metrics-summary {
  margin-bottom: 2rem;
}

.metrics-summary h6 {
  margin: 0 0 1rem 0;
  color: var(--text-primary);
  font-weight: 600;
  text-align: center;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
}

.metric-card {
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 1rem;
  transition: all 0.2s ease;
}

.metric-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.metric-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--border-color);
}

.metric-header h6 {
  margin: 0;
  color: var(--text-primary);
  font-weight: 600;
}

.metric-status {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.metric-status.success {
  background: #d4edda;
  color: #155724;
}

.metric-status.failed {
  background: #f8d7da;
  color: #721c24;
}

.metric-values {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.metric-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.metric-label {
  font-size: 0.85rem;
  color: var(--text-secondary);
}

.metric-value {
  font-weight: 600;
  color: var(--text-primary);
}

/* Cost Comparison Row */
.cost-comparison-row {
  margin-top: 2.5rem;
  padding-top: 2rem;
  border-top: 2px solid var(--border-color);
}

.comparison-title {
  margin: 0 0 1.5rem 0;
  color: var(--text-primary);
  font-weight: 700;
  text-align: center;
  font-size: 1.2rem;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  position: relative;
  padding-bottom: 0.75rem;
}

.comparison-title::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100px;
  height: 3px;
  background: linear-gradient(90deg, transparent, var(--custom-color1, #4CAF50), transparent);
  border-radius: 2px;
}

.comparison-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.25rem;
}

.comparison-card {
  background: var(--card-bg);
  border: 2px solid var(--border-color);
  border-radius: 10px;
  padding: 1.5rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.comparison-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: var(--border-color);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.3s ease;
}

.comparison-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
  border-color: var(--custom-color1, #4CAF50);
}

.comparison-card:hover::before {
  transform: scaleX(1);
}

.comparison-card.best-cost {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.08) 0%, rgba(69, 160, 73, 0.03) 100%);
  border-color: var(--custom-color1, #4CAF50);
  box-shadow: 0 4px 14px rgba(76, 175, 80, 0.15);
}

.comparison-card.best-cost::before {
  transform: scaleX(1);
  background: linear-gradient(90deg, #4CAF50, #45a049);
}

.comparison-card.worst-cost {
  background: linear-gradient(135deg, rgba(244, 67, 54, 0.08) 0%, rgba(211, 47, 47, 0.03) 100%);
  border-color: #f44336;
  box-shadow: 0 4px 14px rgba(244, 67, 54, 0.15);
}

.comparison-card.worst-cost::before {
  transform: scaleX(1);
  background: linear-gradient(90deg, #f44336, #d32f2f);
}

.comparison-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--border-color);
}

.comparison-algo-name {
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--text-primary);
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.comparison-badge {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.comparison-badge.best {
  background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
  color: white;
  box-shadow: 0 2px 6px rgba(76, 175, 80, 0.3);
}

.comparison-badge.worst {
  background: linear-gradient(135deg, #f44336 0%, #d32f2f 100%);
  color: white;
  box-shadow: 0 2px 6px rgba(244, 67, 54, 0.3);
}

.comparison-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.comparison-cost {
  text-align: center;
  padding: 0.75rem;
  background: var(--bg-secondary);
  border-radius: 8px;
}

.cost-amount {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  font-variant-numeric: tabular-nums;
}

.comparison-percentages {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.percentage-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background: var(--bg-secondary);
  border-radius: 6px;
  transition: background 0.2s ease;
}

.percentage-item:hover {
  background: var(--card-bg);
}

.percentage-label {
  font-size: 0.8rem;
  color: var(--text-secondary);
  font-weight: 500;
}

.percentage-value {
  font-size: 0.9rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  padding: 2px 8px;
  border-radius: 4px;
}

.percentage-value.percentage-lower {
  color: #4CAF50;
  background: rgba(76, 175, 80, 0.1);
}

.percentage-value.percentage-higher {
  color: #f44336;
  background: rgba(244, 67, 54, 0.1);
}

.percentage-value.percentage-equal {
  color: var(--text-secondary);
  background: rgba(0, 0, 0, 0.05);
}

.chart-section {
  margin-bottom: 2.5rem;
}

.plant-level-section {
  margin-bottom: 2rem;
}

.plant-level-section h6 {
  margin: 0 0 1.5rem 0;
  color: var(--text-primary);
  font-weight: 600;
  text-align: center;
  font-size: 1.1rem;
}

.chart-section h6 {
  margin: 0 0 1rem 0;
  color: var(--text-primary);
  font-weight: 600;
  text-align: center;
}

.chart-container {
  background: var(--card-bg);
  border-radius: 8px;
  box-shadow: 0 2px 4px var(--shadow-color);
  padding: 1rem;
  padding-bottom: 2rem;
}

/* Total Daily Cost Section */
.total-cost-section {
  margin: 3rem 0 2.5rem 0;
  padding: 2rem;
  background: linear-gradient(135deg, var(--card-bg) 0%, var(--bg-secondary) 100%);
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  border: 1px solid var(--border-color);
}

.section-title {
  margin: 0 0 2rem 0;
  color: var(--text-primary);
  font-weight: 700;
  text-align: center;
  font-size: 1.3rem;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  position: relative;
  padding-bottom: 1rem;
}

.section-title::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 3px;
  background: linear-gradient(90deg, transparent, var(--custom-color1, #4CAF50), transparent);
  border-radius: 2px;
}

.total-cost-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-top: 1.5rem;
}

.total-cost-card {
  background: var(--card-bg);
  border: 2px solid var(--border-color);
  border-radius: 12px;
  padding: 1.75rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.total-cost-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, var(--custom-color1, #4CAF50), #45a049);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.3s ease;
}

.total-cost-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  border-color: var(--custom-color1, #4CAF50);
}

.total-cost-card:hover::before {
  transform: scaleX(1);
}

.total-cost-card.top-performer {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(69, 160, 73, 0.05) 100%);
  border: 2px solid var(--custom-color1, #4CAF50);
  box-shadow: 0 4px 16px rgba(76, 175, 80, 0.2);
}

.total-cost-card.top-performer::before {
  transform: scaleX(1);
  background: linear-gradient(90deg, #4CAF50, #45a049, #66BB6A);
}

.cost-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--border-color);
}

.algorithm-name {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary);
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.best-badge {
  background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);
  }
  50% {
    box-shadow: 0 4px 12px rgba(76, 175, 80, 0.5);
  }
}

.cost-value-container {
  display: flex;
  align-items: baseline;
  justify-content: center;
  margin: 1rem 0;
  gap: 0.25rem;
}

.currency-symbol {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-secondary);
  opacity: 0.8;
}

.cost-value {
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.5px;
}

.total-cost-card.top-performer .cost-value {
  background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.cost-label {
  text-align: center;
  font-size: 0.85rem;
  color: var(--text-tertiary);
  margin-top: 0.5rem;
  font-weight: 500;
  letter-spacing: 0.3px;
}

.chart-controls {
  margin-bottom: 2rem;
  text-align: center;
}

.chart-controls h6 {
  margin: 0 0 1rem 0;
  color: var(--text-primary);
  font-weight: 600;
}

.chart-type-selector {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.chart-type-btn {
  padding: 8px 16px;
  border: 1px solid var(--border-color);
  background: var(--card-bg);
  color: var(--text-secondary);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;
}

.chart-type-btn:hover {
  background: var(--bg-secondary);
  border-color: var(--custom-color1, #4CAF50);
}

.chart-type-btn.active {
  background: var(--custom-color1, #4CAF50);
  color: white;
  border-color: var(--custom-color1, #4CAF50);
}

.device-charts-section {
  margin-bottom: 2rem;
}

.device-charts-section h6 {
  margin: 0 0 1.5rem 0;
  color: var(--text-primary);
  font-weight: 600;
  text-align: center;
}

.device-charts-grid {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.device-chart-container {
  padding-bottom: 2rem;
  background: var(--card-bg);
  border-radius: 8px;
  box-shadow: 0 2px 8px var(--shadow-color);
  overflow: hidden;
  transition: all 0.3s ease;
  width: 100%;
}

.device-chart-container:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.device-chart-header {
  background: var(--bg-secondary);
  padding: 1rem;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.device-chart-header h6 {
  margin: 0;
  color: var(--text-primary);
  font-weight: 600;
  font-size: 0.9rem;
}

.device-info {
  background: var(--custom-color1, #4CAF50);
  color: white;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
}

.device-chart-wrapper {
  padding: 1rem;
  padding-bottom: 2rem;
  min-height: 350px;
  width: 100%;
}

.chart-subtitle {
  margin: 0 0 0.75rem 0;
  color: var(--text-secondary);
  font-weight: 600;
  font-size: 0.9rem;
  text-align: center;
  border-bottom: 1px solid var(--border-color);
  padding-bottom: 0.5rem;
}

.export-section {
  text-align: center;
  margin-top: 2rem;
}

.error-container {
  text-align: center;
  padding: 2rem;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  margin: 1rem 0;
}

.error-text {
  color: var(--text-primary);
  margin: 0 0 1rem 0;
  font-weight: 500;
}

.no-data-container {
  text-align: center;
  padding: 3rem;
  color: var(--text-tertiary);
}

.no-data-text {
  margin: 0;
  font-size: 1.1rem;
}

.custom-button {
  cursor: pointer;
  background: var(--bg-secondary);
  color: var(--text-secondary);
  border: 1px solid var(--border-color);
  border-radius: 5px;
  padding: 8px 16px;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px var(--shadow-color);
}

.custom-button:hover:not(:disabled) {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  background: var(--custom-color1, #4CAF50);
  color: white;
  transform: translateY(-1px);
}

.custom-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.select-all-btn {
  background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
  color: white;
}

.clear-btn {
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a5a 100%);
  color: white;
}

.export-btn {
  background: linear-gradient(135deg, #2196F3 0%, #1976D2 100%);
  color: white;
}

.retry-btn {
  background: linear-gradient(135deg, #FF9800 0%, #F57C00 100%);
  color: white;
}

.compare-btn {
  background: linear-gradient(135deg, #2196F3 0%, #1976D2 100%);
  color: white;
  font-weight: 600;
  padding: 10px 20px;
  font-size: 1rem;
}

@media (max-width: 750px) {
  .bms-simulator-container {
    padding: 1rem;
    margin: 0.5rem 0;
  }

  .version-checkboxes {
    grid-template-columns: 1fr;
  }

  .metrics-grid {
    grid-template-columns: 1fr;
  }

  .cost-comparison-row {
    margin-top: 2rem;
    padding-top: 1.5rem;
  }

  .comparison-title {
    font-size: 1rem;
    margin-bottom: 1.25rem;
  }

  .comparison-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .comparison-card {
    padding: 1.25rem;
  }

  .cost-amount {
    font-size: 1.25rem;
  }

  .comparison-algo-name {
    font-size: 0.85rem;
  }

  .percentage-label {
    font-size: 0.75rem;
  }

  .percentage-value {
    font-size: 0.8rem;
  }

  .version-actions {
    gap: 0.75rem;
  }
  
  .selection-buttons {
    gap: 0.75rem;
  }
  
  .action-divider {
    width: 40px;
    margin: 0.25rem 0;
  }
  
  .compare-btn {
    padding: 8px 16px;
    font-size: 0.9rem;
  }

  .custom-button {
    font-size: 0.8rem;
    padding: 6px 12px;
  }

  .device-charts-grid {
    gap: 1rem;
  }

  .device-chart-wrapper {
    min-height: 300px;
    padding: 0.5rem;
  }

  .device-chart-header {
    padding: 0.75rem;
  }

  .device-chart-header h6 {
    font-size: 0.8rem;
  }

  .total-cost-section {
    padding: 1.5rem 1rem;
    margin: 2rem 0;
  }

  .section-title {
    font-size: 1.1rem;
    margin-bottom: 1.5rem;
    padding-bottom: 0.75rem;
  }

  .total-cost-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .total-cost-card {
    padding: 1.25rem;
  }

  .cost-value {
    font-size: 2rem;
  }

  .currency-symbol {
    font-size: 1.25rem;
  }

  .algorithm-name {
    font-size: 0.85rem;
  }

  .best-badge {
    font-size: 0.65rem;
    padding: 3px 10px;
  }
}

/* Today's Date Warning Styles */
.today-warning {
  background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
  border: 2px solid #ffc107;
  border-radius: 12px;
  padding: 1.5rem;
  margin: 1rem 0;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 4px 12px rgba(255, 193, 7, 0.2);
  animation: warningPulse 2s ease-in-out infinite;
}

.warning-icon {
  font-size: 2rem;
  flex-shrink: 0;
}

.warning-content h6 {
  margin: 0 0 0.5rem 0;
  color: #856404;
  font-weight: 600;
  font-size: 1.1rem;
}

.warning-content p {
  margin: 0;
  color: #856404;
  font-size: 0.95rem;
  line-height: 1.4;
}

@keyframes warningPulse {
  0%, 100% {
    box-shadow: 0 4px 12px rgba(255, 193, 7, 0.2);
  }
  50% {
    box-shadow: 0 6px 16px rgba(255, 193, 7, 0.3);
  }
}

/* Enhanced Loading Styles */
.loading-container {
  background: var(--card-bg);
  border: 2px solid var(--border-color);
  border-radius: 16px;
  padding: 3rem 2rem;
  margin: 2rem 0;
  text-align: center;
  box-shadow: 0 8px 24px var(--shadow-color);
}

.loading-content {
  max-width: 400px;
  margin: 0 auto;
}

.loading-spinner-large {
  position: relative;
  width: 80px;
  height: 80px;
  margin: 0 auto 2rem auto;
}

.spinner-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 4px solid transparent;
  border-top: 4px solid #007bff;
  border-radius: 50%;
  animation: spin 1.5s linear infinite;
}

.spinner-ring:nth-child(2) {
  width: 60px;
  height: 60px;
  top: 10px;
  left: 10px;
  border-top-color: #28a745;
  animation-delay: -0.5s;
}

.spinner-ring:nth-child(3) {
  width: 40px;
  height: 40px;
  top: 20px;
  left: 20px;
  border-top-color: #ffc107;
  animation-delay: -1s;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-title {
  color: var(--text-primary);
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
}

.loading-text {
  color: var(--text-secondary);
  font-size: 1rem;
  margin: 0 0 1.5rem 0;
  line-height: 1.4;
}

.loading-progress {
  margin-top: 1.5rem;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background-color: var(--bg-tertiary);
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.5rem;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #007bff, #28a745, #ffc107);
  background-size: 200% 100%;
  animation: progressMove 2s ease-in-out infinite;
  border-radius: 4px;
}

@keyframes progressMove {
  0% {
    background-position: 0% 50%;
    width: 0%;
  }
  50% {
    background-position: 100% 50%;
    width: 70%;
  }
  100% {
    background-position: 0% 50%;
    width: 100%;
  }
}

.progress-text {
  color: var(--text-secondary);
  font-size: 0.9rem;
  font-style: italic;
}

/* Mobile Responsive for New Styles */
@media (max-width: 750px) {
  .today-warning {
    flex-direction: column;
    text-align: center;
    padding: 1rem;
    gap: 0.75rem;
  }
  
  .warning-icon {
    font-size: 1.5rem;
  }
  
  .loading-container {
    padding: 2rem 1rem;
    margin: 1rem 0;
  }
  
  .loading-spinner-large {
    width: 60px;
    height: 60px;
  }
  
  .spinner-ring:nth-child(2) {
    width: 45px;
    height: 45px;
    top: 7.5px;
    left: 7.5px;
  }
  
  .spinner-ring:nth-child(3) {
    width: 30px;
    height: 30px;
    top: 15px;
    left: 15px;
  }
  
  .loading-title {
    font-size: 1.25rem;
  }
  
  .loading-text {
    font-size: 0.9rem;
  }
}
</style>
